import {Component, inject, Input, OnInit, Output, EventEmitter, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {GtNgDatepickerComponent} from '../../shared/commons/gt-ng-datepicker/gt-ng-datepicker.component';
import {finalize, catchError} from 'rxjs/operators';
import {forkJoin, of} from 'rxjs';
import {RoleAssignApiService} from './role-assign-api.service';
import {RoleAssignUserSelectModal} from './role-assign-user-select-modal';
import {UserBusinessApiService} from '../user-business/user-business-api.service';
import {UserCertificateApiService} from '../user-certificate/user-certificate-api.service';
import {
  CertificateItemDto,
  RoleAssignmentAction,
  RoleAssignmentDto,
  RoleAssignmentUserLookupDto,
  UserBusinessDto
} from '../../../models/generated-models';
import {roleAssignmentActionLabel} from '../../app.constants';
import {CurrentUserContextService} from '../../shared/services/current-user-context.service';

export interface CertificateOption {
  id: number;
  certificateNo?: string;
  standardCode?: string;
  standardName?: string;
  productName?: string;
  label: string;
}

export interface BusinessOption {
  id: number;
  farmId?: number;
  title: string;
  label: string;
  certificates: CertificateOption[];
}

export interface ActionOption {
  action: RoleAssignmentAction;
  label: string;
}

@Component({
  selector: 'app-role-assign-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    GtNgDatepickerComponent
  ],
  templateUrl: './role-assign-modal.html',
  styleUrl: './role-assign-modal.scss',
})
export class RoleAssignModal implements OnInit {
  @Output() saved = new EventEmitter<any>();
  /** When set, the modal is in edit mode and prefills from this grant. */
  @Input() editValue: RoleAssignmentDto | null = null;

  private readonly api = inject(RoleAssignApiService);
  private readonly businessApi = inject(UserBusinessApiService);
  private readonly certificateApi = inject(UserCertificateApiService);
  private readonly modalService = inject(NgbModal);
  private readonly currentUserContext = inject(CurrentUserContextService);

  protected readonly roleAssignmentActionLabel = roleAssignmentActionLabel;

  addForm: FormGroup;

  // Recipient lookup state — signals (this app is zoneless).
  protected recipientTaxId = '';
  protected readonly selectedRecipient = signal<RoleAssignmentUserLookupDto | null>(null);
  protected readonly searching = signal(false);
  protected readonly recipientError = signal('');

  // Business scoping state.
  protected readonly businesses = signal<BusinessOption[]>([]);
  protected readonly loadingBusinesses = signal(false);
  protected readonly selectedBusinessIds = signal<number[]>([]);

  // Certificate selection state scoped by business id.
  protected readonly selectedCertificatesByBusiness = signal<Map<number, CertificateOption[]>>(new Map());

  // Actions selection state.
  protected readonly actionOptions = signal<ActionOption[]>([
    { action: 'PURCHASE_SALE', label: roleAssignmentActionLabel('PURCHASE_SALE') }
  ]);
  protected readonly selectedActions = signal<RoleAssignmentAction[]>(['PURCHASE_SALE']);
  protected readonly submitted = signal(false);

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder
  ) {
    this.addForm = this.fb.group({
      assigner: [{ value: '', disabled: true }],
      assignee: ['', [Validators.required]],
      startDate: [null],
      endDate: [null]
    });
  }

  ngOnInit(): void {
    if (this.editValue) {
      this.applyEditValue(this.editValue);
    }
    this.currentUserContext.load().subscribe({
      next: (profile) => {
        if (profile) {
          const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.username || 'ผู้มอบสิทธิ์';
          this.addForm.patchValue({ assigner: name });
        }
      },
      error: () => {}
    });

    this.loadingBusinesses.set(true);
    forkJoin({
      businesses: this.businessApi.listBusinesses(),
      certificates: this.certificateApi.listCertificates().pipe(catchError(() => of([] as CertificateItemDto[])))
    }).pipe(finalize(() => this.loadingBusinesses.set(false))).subscribe({
      next: ({businesses, certificates}) => {
        const rawCerts = certificates ?? [];
        const rawBizList = businesses ?? [];
        const options = rawBizList
          .map((item) => this.toBusinessOption(item, rawCerts, rawBizList.length))
          .filter((item): item is BusinessOption => item !== null);
        this.businesses.set(options);

        if (this.editValue) {
          const editBizIds = this.editValue.businessIds ?? [];
          this.selectedBusinessIds.set([...editBizIds]);
          const editCodes = (this.editValue.standardCode ?? '')
            .split(',')
            .map((s) => {
              return s.trim().toLowerCase();
            })
            .filter((s) => {
              return !!s;
            });
          const editNos = (this.editValue.certificateNo ?? '')
            .split(',')
            .map((s) => {
              return s.trim().toLowerCase();
            })
            .filter((s) => {
              return !!s;
            });
          const editCertId = this.editValue.certificateId;

          if (editCertId || editCodes.length > 0 || editNos.length > 0) {
            const map = new Map<number, CertificateOption[]>();
            for (const bizId of editBizIds) {
              const biz = options.find((b) => {
                return b.id === bizId;
              });
              if (!biz) {
                continue;
              }
              const matched = biz.certificates.filter((c) => {
                const code = c.standardCode?.trim().toLowerCase();
                const no = c.certificateNo?.trim().toLowerCase();
                if (editCertId && c.id === editCertId) {
                  return true;
                }
                if (no && editNos.includes(no)) {
                  return true;
                }
                if (code && editCodes.includes(code)) {
                  return true;
                }
                return false;
              });
              if (matched.length > 0) {
                map.set(bizId, matched);
              } else if (this.editValue.standardCode || this.editValue.certificateNo || editCertId) {
                map.set(bizId, [{
                  id: editCertId ?? 0,
                  certificateNo: this.editValue.certificateNo,
                  standardCode: this.editValue.standardCode,
                  standardName: this.editValue.standardName,
                  label: this.editValue.standardName || this.editValue.standardCode || ''
                }]);
              }
            }
            this.selectedCertificatesByBusiness.set(map);
          }
        }
      },
      error: () => {
        this.businesses.set([]);
      },
    });
  }

  private applyEditValue(grant: RoleAssignmentDto): void {
    this.recipientTaxId = grant.delegateTaxId ?? '';
    this.selectedRecipient.set({
      id: grant.delegateUserId,
      name: grant.delegateName,
      taxId: grant.delegateTaxId,
      userTypes: [],
    });
    if (grant.actions && grant.actions.length > 0) {
      this.selectedActions.set([...grant.actions]);
    }
    this.addForm.patchValue({
      assignee: grant.delegateTaxId ?? '',
      startDate: grant.startsOn ?? null,
      endDate: grant.endsOn ?? null,
    });
  }

  searchRecipient(): void {
    const taxId = this.recipientTaxId.trim();
    this.recipientError.set('');
    if (!taxId || this.searching()) {
      return;
    }
    this.searching.set(true);
    this.api.lookupUser(taxId).pipe(finalize(() => this.searching.set(false))).subscribe({
      next: (users) => {
        if (users.length === 0) {
          this.clearRecipientSelection('ไม่พบข้อมูลผู้ใช้งานจากเลขประจำตัวที่ระบุ กรุณาตรวจสอบอีกครั้ง');
          return;
        }
        if (users.length === 1) {
          if (users[0].invitationAccepted) {
            this.clearRecipientSelection('ผู้ใช้งานนี้ได้ตอบรับคำเชิญการมอบสิทธิ์แล้ว');
            return;
          }
          this.selectRecipient(users[0]);
          return;
        }

        const modalRef = this.modalService.open(RoleAssignUserSelectModal, {
          centered: true,
          size: 'md',
          backdrop: 'static',
        });
        modalRef.componentInstance.users = users;
        modalRef.result.then(
          (selectedUser) => {
            if (selectedUser) {
              if (selectedUser.invitationAccepted) {
                this.clearRecipientSelection('ผู้ใช้งานนี้ได้ตอบรับคำเชิญการมอบสิทธิ์แล้ว');
                return;
              }
              this.selectRecipient(selectedUser);
            }
          },
          () => {}
        );
      },
      error: () => {
        this.clearRecipientSelection('ไม่พบข้อมูลผู้ใช้งานจากเลขประจำตัวที่ระบุ กรุณาตรวจสอบอีกครั้ง');
      },
    });
  }

  private selectRecipient(user: RoleAssignmentUserLookupDto): void {
    this.selectedRecipient.set(user);
    this.addForm.patchValue({assignee: user.taxId ?? this.recipientTaxId.trim()});
    this.recipientError.set('');
  }

  private clearRecipientSelection(errorMessage = ''): void {
    this.selectedRecipient.set(null);
    this.addForm.patchValue({assignee: ''});
    this.recipientError.set(errorMessage);
  }

  removeRecipient(): void {
    this.selectedRecipient.set(null);
    this.recipientTaxId = '';
    this.recipientError.set('');
    this.addForm.patchValue({assignee: ''});
  }

  protected isBusinessSelected(id: number): boolean {
    return this.selectedBusinessIds().includes(id);
  }

  protected toggleBusiness(id: number, checked: boolean): void {
    const current = this.selectedBusinessIds();
    const updated = checked ? Array.from(new Set([...current, id])) : current.filter((item) => item !== id);
    this.selectedBusinessIds.set(updated);

    if (!checked) {
      const map = new Map(this.selectedCertificatesByBusiness());
      if (map.has(id)) {
        map.delete(id);
        this.selectedCertificatesByBusiness.set(map);
      }
    }
  }

  protected certTrackKey(cert: CertificateOption, index: number): string {
    return `${cert.id || index}-${cert.standardCode || ''}-${cert.certificateNo || ''}`;
  }

  private isSameCertificate(a: CertificateOption, b: CertificateOption): boolean {
    if (a.id && b.id && a.id === b.id) {
      return true;
    }
    if (a.certificateNo && b.certificateNo && a.certificateNo.trim().toLowerCase() === b.certificateNo.trim().toLowerCase()) {
      return true;
    }
    if (a.standardCode && b.standardCode && a.standardCode.trim().toLowerCase() === b.standardCode.trim().toLowerCase()) {
      return true;
    }
    return false;
  }

  protected isCertificateSelected(business: BusinessOption, cert: CertificateOption): boolean {
    const list = this.selectedCertificatesByBusiness().get(business.id);
    if (!list || list.length === 0) {
      return false;
    }
    return list.some((sel) => {
      return this.isSameCertificate(sel, cert);
    });
  }

  protected toggleCertificate(business: BusinessOption, cert: CertificateOption, checked: boolean): void {
    const map = new Map(this.selectedCertificatesByBusiness());
    const currentList = map.get(business.id) ?? [];

    if (checked) {
      const alreadyIn = currentList.some((sel) => {
        return this.isSameCertificate(sel, cert);
      });
      if (!alreadyIn) {
        map.set(business.id, [...currentList, cert]);
        this.selectedCertificatesByBusiness.set(map);
      }
      if (!this.isBusinessSelected(business.id)) {
        this.selectedBusinessIds.set(Array.from(new Set([...this.selectedBusinessIds(), business.id])));
      }
    } else {
      const nextList = currentList.filter((sel) => {
        return !this.isSameCertificate(sel, cert);
      });
      if (nextList.length > 0) {
        map.set(business.id, nextList);
      } else {
        map.delete(business.id);
      }
      this.selectedCertificatesByBusiness.set(map);
    }
  }

  protected hasSelectedCertificate(business: BusinessOption): boolean {
    if (!business || !business.certificates || business.certificates.length === 0) {
      return false;
    }
    const list = this.selectedCertificatesByBusiness().get(business.id);
    return !!list && list.length > 0;
  }

  /**
   * A certificate under a business is optional (work item 173): a company, an exporter or a
   * cooperative may have none, and the delegate acts for the business as a whole. The only
   * requirement is at least one business.
   */
  protected get allSelectedBusinessesValid(): boolean {
    return this.selectedBusinessIds().length > 0;
  }

  protected isActionSelected(action: RoleAssignmentAction): boolean {
    return this.selectedActions().includes(action);
  }

  protected toggleAction(action: RoleAssignmentAction, checked: boolean): void {
    const current = this.selectedActions();
    this.selectedActions.set(
      checked ? Array.from(new Set([...current, action])) : current.filter((item) => {
        return item !== action;
      })
    );
  }

  protected get canSubmit(): boolean {
    return (
      this.addForm.valid &&
      this.allSelectedBusinessesValid &&
      this.selectedActions().length > 0
    );
  }

  onSave(): void {
    this.submitted.set(true);
    if (!this.canSubmit) {
      this.addForm.markAllAsTouched();
      return;
    }
    const value = this.addForm.getRawValue();
    const recipient = this.selectedRecipient();
    const allCerts: CertificateOption[] = [];
    for (const bizId of this.selectedBusinessIds()) {
      const list = this.selectedCertificatesByBusiness().get(bizId) ?? [];
      for (const c of list) {
        allCerts.push(c);
      }
    }

    const standardCodes = Array.from(new Set(allCerts.map((c) => {
      return c.standardCode?.trim();
    }).filter((s): s is string => {
      return !!s;
    })));
    const standardNames = Array.from(new Set(allCerts.map((c) => {
      return (c.standardName || c.label)?.trim();
    }).filter((s): s is string => {
      return !!s;
    })));
    const certNos = Array.from(new Set(allCerts.map((c) => {
      return c.certificateNo?.trim();
    }).filter((s): s is string => {
      return !!s;
    })));
    const firstCert = allCerts[0] ?? null;

    this.saved.emit({
      delegateTaxId: value.assignee,
      delegateUserId: recipient?.id,
      businessIds: this.selectedBusinessIds(),
      actions: this.selectedActions(),
      certificateId: firstCert?.id,
      certificateNo: certNos.join(', ') || firstCert?.certificateNo,
      standardCode: standardCodes.join(', ') || firstCert?.standardCode,
      standardName: standardNames.join(', ') || firstCert?.standardName,
      startsOn: value.startDate || null,
      endsOn: value.endDate || null,
    });
    this.activeModal.close();
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  private toBusinessOption(item: UserBusinessDto, allCertificates: CertificateItemDto[], totalBizCount: number): BusinessOption | null {
    const id = item.farmId ?? item.id ?? null;
    if (id === null) {
      return null;
    }

    const matchedCerts = allCertificates.filter((cert) => {
      if (item.farmId != null && cert.farmId != null && Number(cert.farmId) === Number(item.farmId)) {
        return true;
      }
      if (item.id != null && cert.farmId != null && Number(cert.farmId) === Number(item.id)) {
        return true;
      }
      if (item.certificates?.some((ref) => (ref.certificateId != null && ref.certificateId === cert.id) ||
          (ref.certificateNo && cert.certificateNo && ref.certificateNo.trim() === cert.certificateNo.trim()))) {
        return true;
      }
      if (item.certificateNos?.some((no) => cert.certificateNo && no.trim() === cert.certificateNo.trim())) {
        return true;
      }
      return false;
    });

    const certList = matchedCerts.length > 0
      ? matchedCerts
      : (totalBizCount === 1 ? allCertificates : []);

    const seen = new Set<string>();
    const uniqueCerts = certList.filter((cert) => {
      const key = `${cert.id ?? ''}-${cert.certificateNo ?? ''}-${cert.standardCode ?? ''}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    const certOptions: CertificateOption[] = uniqueCerts.map((cert) => ({
      id: cert.id ?? 0,
      certificateNo: cert.certificateNo,
      standardCode: cert.standardCode,
      standardName: cert.standardName,
      productName: cert.productName,
      label: `${cert.standardName || cert.standardCode || 'มาตรฐาน'} (${cert.standardCode || '-'}) - เลขที่: ${cert.certificateNo || '-'}`
    }));

    return {
      id,
      farmId: item.farmId ?? undefined,
      title: item.title?.trim() || '-',
      label: item.taxId ? `เลขผู้เสียภาษี ${item.taxId}` : '',
      certificates: certOptions
    };
  }
}
